import React from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ImageSourcePropType,
  Image,
  StyleProp,
  ViewStyle,
} from "react-native";
import { colors } from "src/assets";
import { stringDateToAge, stringDateFullDynamicFormat } from "src/utils/date";
import { Icon } from "src/components";
import { UserStatus, Gender } from "src/domains";
import { genderList } from "src/constant";
import { VerificationCheckIcon } from "./components";
import LocalText from "src/local";

export type UserCardProps = {
  contanerStyle?: StyleProp<ViewStyle>;
  source?: ImageSourcePropType;
  status?: UserStatus;
  name?: string;
  birthday?: string;
  prefecture?: string;
  gender?: Gender;
  isVerified?: boolean;
  lastLoginAt?: string;
  onPress?: () => void;
};

export const UserCard: React.FC<UserCardProps> = ({
  contanerStyle,
  status,
  source,
  name,
  birthday,
  prefecture,
  gender,
  isVerified,
  lastLoginAt,
  onPress,
}) => {
  const avatarStyle = {
    borderRadius: 31,
    ...(status === UserStatus.LOGOUT
      ? {
          borderWidth: 3,
          borderColor: colors.chokuGray6,
        }
      : {}),
    ...(status === UserStatus.OFFERABLE
      ? {
          borderWidth: 3,
          borderColor: colors.chokuGreen,
        }
      : {}),
    ...(status === UserStatus.ACTIVE
      ? {
          borderWidth: 3,
          borderColor: colors.orange,
        }
      : {}),
    ...(status === UserStatus.IN_CALL
      ? {
          borderWidth: 3,
          borderColor: colors.chokuRed,
        }
      : {}),
  };
  const badgeContainerStyle = {
    ...(status === UserStatus.LOGOUT
      ? {
          backgroundColor: colors.chokuGray6,
        }
      : {}),
    ...(status === UserStatus.OFFERABLE
      ? {
          backgroundColor: colors.chokuGreen,
        }
      : {}),
    ...(status === UserStatus.ACTIVE
      ? {
          backgroundColor: colors.orange,
        }
      : {}),
    ...(status === UserStatus.IN_CALL
      ? {
          backgroundColor: colors.chokuRed,
        }
      : {}),
  };
  let detail = "";
  if (gender && gender !== Gender.UNDEFINED) {
    detail = `${genderList.find((item) => item.key === gender)?.label}`;
  }
  if (birthday) {
    detail = `${detail}(${stringDateToAge(birthday)}代)`;
  }
  if (prefecture) {
    detail = `${detail} / ${prefecture}`;
  }

  const content = () => {
    return (
      <React.Fragment>
        <View>
          {source ? (
            <Image style={[styles.image, avatarStyle]} source={source} />
          ) : (
            <View style={[styles.imageEmpty, avatarStyle]}>
              <Icon name={"person"} color={colors.chokuGray5} size={32} />
            </View>
          )}
        </View>
        <View
          style={{
            flex: 1,
            flexDirection: "column",
            justifyContent: "space-between",
            // alignItems: 'flex-start',
            paddingLeft: 10,
          }}
        >
          {status && (
            <View
              style={{ flexDirection: "row", justifyContent: "flex-start" }}
            >
              <View style={[styles.badgeContainer, badgeContainerStyle]}>
                {status === UserStatus.LOGOUT && (
                  <Text style={styles.badge}>
                    {
                      LocalText.components.offerListItem.offerAvatar
                        .userStatusLogout
                    }
                  </Text>
                )}
                {status === UserStatus.OFFERABLE && (
                  <Text style={styles.badge}>
                    {
                      LocalText.components.offerListItem.offerAvatar
                        .userStatusOfferable
                    }
                  </Text>
                )}
                {status === UserStatus.ACTIVE && (
                  <Text style={styles.badge}>
                    {
                      LocalText.components.offerListItem.offerAvatar
                        .userStatusActive
                    }
                  </Text>
                )}
                {status === UserStatus.IN_CALL && (
                  <Text style={styles.badge}>
                    {
                      LocalText.components.offerListItem.offerAvatar
                        .userStatusInCall
                    }
                  </Text>
                )}
              </View>
            </View>
          )}
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {isVerified !== undefined && (
              <VerificationCheckIcon
                style={{ marginRight: 8 }}
                color={isVerified ? colors.chokuRed : "#E3E3E3"}
              />
            )}
            <View style={{ flex: 1 }}>
              <Text style={styles.name}>{name}さん </Text>
            </View>
          </View>

          <Text style={styles.detail}>{detail}</Text>

          {lastLoginAt && (
            <Text style={styles.detail}>
              最終アクセス: {stringDateFullDynamicFormat(lastLoginAt)}
            </Text>
          )}
        </View>
        {onPress ? (
          <View style={styles.icon}>
            <svg width="7.63" height="12.777" viewBox="0 0 7.63 12.777">
              <path
                d="M673.474,1210.877a1.133,1.133,0,0,1,.8.333l5.358,5.358a1.136,1.136,0,0,1,0,1.607l-5.147,5.147a1.136,1.136,0,1,1-1.607-1.607l4.344-4.344-4.554-4.554a1.136,1.136,0,0,1,.8-1.94Z"
                transform="translate(-672.338 -1210.877)"
                fill="#a5a5a5"
              />
            </svg>
          </View>
        ) : null}
      </React.Fragment>
    );
  };

  if (!onPress) {
    return <View style={[styles.container, contanerStyle]}>{content()}</View>;
  }

  return (
    <TouchableOpacity
      style={[styles.container, contanerStyle]}
      onPress={onPress}
    >
      {content()}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    backgroundColor: colors.chokuGray12,
    borderRadius: 4,
    padding: 16,
  },
  image: {
    width: 62,
    height: 62,
  },
  imageEmpty: {
    backgroundColor: colors.gray1,
    width: 62,
    height: 62,
    justifyContent: "center",
    alignItems: "center",
  },
  badgeContainer: {
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 4,
  },
  badge: {
    color: colors.white,
    fontSize: 10,
    fontWeight: "bold",
  },
  name: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333333",
  },
  detail: {
    fontSize: 12,
    color: colors.chokyGray10,
  },
  icon: {
    justifyContent: "center",
    alignItems: "center",
  },
});
